import {
  deraRequiredComment,
  DeraOneToFiveScaleOptions,
  DeraQuestionType,
  DeraSubsectionLookup,
} from './dera-questions.interface';

/**
 * DERA section schema. Each subsection has a keyed `questions` map that the UI renders in order.
 *
 * Question format:
 * - `type`: CheckboxList | Radio | Scale | Comment (picks the renderer), or Custom for a slot the UI fills
 *   with its own component via `<ng-template cmDeraCustom="questionKey">` (no answer is stored for it).
 * - `options`: keyed choices for CheckboxList/Radio/Scale. The stored value is the option key.
 *   Reuse `DeraOneToFiveScaleOptions` for the standard 1-5 + "?" scale.
 * - `required` + `requiredMessage`: field must be answered; shows the message and a red outline
 *   when "Click to show missing fields" / "Complete & close DERA" runs.
 * - `requiredWhen: { question, includesOption }`: required only when a sibling question in the same
 *   subsection holds a value (checkbox `includes` or radio/scale `equals`).
 * - `comment`: attaches a comment box to the question. Pass `true`/`false` for a plain optional
 *   comment, or a config object (`placeholder`, `required`, `requiredWhen.includesOption`). Use
 *   `deraRequiredComment` for the common "Other/? makes the comment mandatory" case. The comment is
 *   stored on the same answer under `comment`.
 * - `placeholder`: instruction text for standalone Comment questions.
 *
 * Option lists are best-effort from the mockups and should be confirmed against final content.
 */
export const DeraBackgroundSubsections: { [key: string]: DeraSubsectionLookup } = {
  livingSituation: {
    displayValue: 'Living Situation',
    questions: {
      currentLivingSituation: {
        displayValue: 'Current Living Situation - select all that apply',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Current living situation is required',
        options: {
          ownPlace: { displayValue: 'Living alone, in their own place' },
          withFamily: { displayValue: 'Living with family (spouse/partner, parents, children, other relatives)' },
          withRoommates: { displayValue: 'Living with roommates or people who are not family members' },
          withCaregiver: { displayValue: 'Living with a caregiver or guardian who is not a family member' },
          groupHome: { displayValue: 'Living in a group home' },
          assistedLiving: { displayValue: 'Living in an assisted living or care home where staff are available' },
          nursingHome: { displayValue: 'Living in a nursing home or long-term care home' },
          rehabRecovery: { displayValue: 'Staying in a rehab or recovery centre' },
          emergencyShelter: { displayValue: 'Staying in an emergency shelter' },
          temporaryHousing: { displayValue: 'In temporary housing or moving between places' },
          correctionalFacility: { displayValue: 'Living in a correctional facility/institution' },
          noPlaceToLive: { displayValue: 'No place to live right now' },
          other: { displayValue: 'Other, please specify (requires text in the comment box)' },
        },
        comment: deraRequiredComment('other', 'Add any relevant details regarding the person’s housing situation'),
      },
      housingStability: {
        displayValue: 'Housing Stability',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Housing stability is required',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment('unknown', 'Add any relevant details regarding the person’s housing stability'),
        tooltip: {
          title: 'Housing Stability',
          items: [
            'Unhoused - homeless, couch surfing',
            'At risk of eviction or utility disconnection / accessing short-term shelter but no immediate long-term housing options / unable to manage ongoing housing costs or rental arrears / health or safety concerns',
            'Current housing is stable but not suitable to their circumstances, e.g., disability adaptation, changes to family household / in transitional housing / requires long term housing or subsidized housing / has secured housing and needs support to obtain',
            "Has stable and long-term housing / no risks to current housing / may want to move but doesn't need to / managing rent and utilities monthly / accessing subsidy / needs one-time housing support",
            'Long term sustainable housing / does not require subsidy / has a network of support / ability to choose housing options',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
    },
  },
  gettingAround: {
    displayValue: 'Getting Around',
    questions: {
      usualWaysOfGettingAround: {
        displayValue: 'Usual ways of getting around - select all that apply',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Usual ways of getting around is required',
        options: {
          personalVehicle: { displayValue: 'Personal vehicle' },
          ridesFromFriendsFamily: { displayValue: 'Rides from friends or family' },
          publicTransit: { displayValue: 'Public transit (bus, train, LRT)' },
          doorToDoorTransit: {
            displayValue: 'Door-to-door transit for people with disabilities (e.g., DATS, CTA)',
          },
          taxiRideShare: { displayValue: 'Taxi or ride share (e.g., Uber, Lyft)' },
          walking: { displayValue: 'Walking' },
          biking: { displayValue: 'Biking' },
          noWayToGetAround: { displayValue: 'Does not have a way to get around' },
          other: { displayValue: 'Other, please specify (requires text in comment box)' },
        },
        comment: deraRequiredComment(
          'other',
          'Add any relevant details regarding the person’s usual modes of transportation'
        ),
      },
      canGetAroundWithoutHelp: {
        displayValue: 'Can get around without help from others?',
        type: DeraQuestionType.Radio,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          yes: { displayValue: 'Yes' },
          sometimes: { displayValue: 'Sometimes' },
          no: { displayValue: 'No' },
        },
        comment: {
          placeholder:
            'Add any relevant details regarding the person’s ability to get around independently, including factors that create transportation barriers, e.g., health, scheduling considerations, destination, weather/season, etc.',
        },
      },
      transportationSuitability: {
        displayValue: 'Suitability of transportation for commuting',
        type: DeraQuestionType.Radio,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          noBarriers: { displayValue: 'No transportation barriers when commuting to work' },
          someBarriers: {
            displayValue:
              'Some transportation barriers when commuting to work; please specify (requires text in comment box)',
          },
        },
        comment: deraRequiredComment(
          'someBarriers',
          'Describe any transportation barriers when commuting to work regularly'
        ),
      },
    },
  },
  communitySupports: {
    displayValue: 'Community Supports and Daily Management',
    questions: {
      naturalSupportNetwork: {
        displayValue: 'Natural support network (friends and family) - select all that apply',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Natural support network is required',
        options: {
          spousePartner: { displayValue: 'Spouse or partner' },
          friendsRoommates: { displayValue: 'Friends and/or roommates' },
          parentsGuardians: { displayValue: 'Parent(s) and/or legal guardian(s)' },
          otherFamily: { displayValue: 'Other family members' },
          neighbours: { displayValue: 'Neighbour(s)' },
          otherCommunity: { displayValue: 'Other community members' },
          noNaturalSupport: { displayValue: 'Does not have a natural support network right now' },
          other: { displayValue: 'Other, please specify (requires text in comment box)' },
        },
        comment: deraRequiredComment(
          'other',
          'Comment on the strength of the person’s natural support network, i.e., friends and family they are in regular contact with and can ask for help if needed\nIdentify any support gaps, e.g., isolation, no natural supports, recently moved and supports live far away'
        ),
      },
      dailyRoutine: {
        displayValue:
          'Ability to manage daily routine, e.g., waking up on time, personal care, keeping appointments, etc.',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Ability to manage daily routine is required',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment(
          'unknown',
          'Identify any personal management challenges relevant to finding and maintaining employment'
        ),
        tooltip: {
          title: 'Ability to manage daily routine',
          items: [
            'Need to develop the ability to do this',
            'Can do this in some situations with support or guidance',
            'Can do this with support or guidance',
            'Can do this independently some of the time',
            'Can do this independently all the time',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      formalSupports: {
        displayValue: 'Formal supports / regular services - select all that apply',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Formal supports / regular services is required',
        options: {
          homecare: { displayValue: 'Homecare / daily living support' },
          rehabilitation: { displayValue: 'Rehabilitation services' },
          disabilitySupports: { displayValue: 'Disability supports' },
          respiteCare: { displayValue: 'Respite care for someone the individual regularly cares for' },
          communityProgramming: { displayValue: 'Community programming (not volunteering)' },
          probationParole: { displayValue: 'Probation or parole officer' },
          notApplicable: { displayValue: 'N/A - no need for formal supports' },
          other: { displayValue: 'Other, please specify (requires text in comment box)' },
        },
        comment: deraRequiredComment('other', 'Identify supports and services in place and any unmet support needs'),
      },
    },
  },
  familyHealthCaregiving: {
    displayValue: 'Family Health and Caregiving Responsibilities',
    questions: {
      caregivingResponsibilities: {
        displayValue: 'Caregiving responsibilities',
        type: DeraQuestionType.Radio,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          affectsAvailability: {
            displayValue:
              'Caregiving responsibilities affect the individual’s availability for work (requires text in comment box)',
          },
          doesNotAffectAvailability: {
            displayValue: 'Caregiving responsibilities do not affect the individual’s availability for work',
          },
          none: { displayValue: 'None - individual does not provide care to anyone' },
        },
        comment: deraRequiredComment(
          'affectsAvailability',
          'Indicate who the person cares for and how this affects their schedule, e.g., can only work evenings.\nNote availability of other caregivers / respite care, etc.'
        ),
      },
    },
  },
};
